import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchBlog, imgUrl } from '../api';
import BlogsNews from './BlogsNews';

// ─── useInView ─────────────────────────────────────────────────────────────────
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ─── Content block ─────────────────────────────────────────────────────────────
function ContentBlock({ block, index }) {
  const [ref, inView] = useInView(0.1);
  const base  = `transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`;
  const delay = { transitionDelay: `${Math.min(index * 60, 300)}ms` };

  if (block.type === 'heading') {
    return (
      <h2 ref={ref}
        className={`font-display font-normal text-stone-900 leading-snug mt-6 mb-1 ${base}`}
        style={{ ...delay, fontSize: 'clamp(1.5rem, 2.8vw, 2.2rem)' }}>
        {block.text}
      </h2>
    );
  }
  if (block.type === 'subheading') {
    return (
      <h3 ref={ref}
        className={`font-display font-normal text-stone-700 leading-snug mt-4 mb-1 ${base}`}
        style={{ ...delay, fontSize: 'clamp(1.1rem, 2vw, 1.6rem)' }}>
        {block.text}
      </h3>
    );
  }
  if (block.type === 'quote') {
    return (
      <blockquote ref={ref} className={`border-l-2 border-gold pl-6 my-2 ${base}`} style={delay}>
        <p className="font-display text-stone-500 italic font-light leading-relaxed"
          style={{ fontSize: 'clamp(1.05rem, 1.8vw, 1.25rem)' }}>
          {block.text}
        </p>
      </blockquote>
    );
  }
  // Paragraph — Didact Gothic for body, comfortable reading size
  return (
    <p ref={ref}
      className={`font-body text-stone-600 leading-relaxed ${base}`}
      style={{ ...delay, fontSize: 'clamp(0.875rem, 1.4vw, 1rem)' }}>
      {block.text}
    </p>
  );
}

export default function BlogDetailPage() {
  const { slug }  = useParams();
  const navigate  = useNavigate();
  const [blog,    setBlog]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0 });
    fetchBlog(slug)
      .then(d => setBlog(d.data))
      .catch(() => setBlog(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Format date
  const displayDate = blog?.publishedAt
    ? new Date(blog.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : blog?.createdAt
      ? new Date(blog.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
      : '';

  if (loading) return (
    <div className="min-h-screen bg-white pt-20 animate-pulse">
      <div className="max-w-7xl mx-auto px-4 md:px-0 pt-10">
        <div className="w-full h-64 md:h-80 bg-stone-100 rounded-br-[40px]" />
      </div>
      <div className="max-w-7xl mx-auto px-4 py-10 space-y-4">
        <div className="h-3 bg-stone-100 rounded w-24" />
        <div className="h-8 bg-stone-100 rounded w-3/4" />
        <div className="h-px bg-stone-100 mt-6" />
        {[...Array(5)].map((_, i) => <div key={i} className="h-4 bg-stone-100 rounded" style={{ width: `${80 + Math.random() * 20}%` }} />)}
      </div>
    </div>
  );

  if (!blog) return (
    <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
      <div className="text-center">
        <p className="font-display text-stone-400 text-2xl font-light mb-4">Blog not found</p>
        <button onClick={() => navigate('/all-blogs')}
          className="font-body text-[11px] tracking-widest uppercase text-gold hover:text-stone-900 transition-colors">
          ← Back to Blogs
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white pt-20">

      {/* Hero image — aligned with page content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 lg:px-20 pt-10 md:pt-14">
        <div className="w-full overflow-hidden rounded-br-[40px]" style={{ height: 'clamp(200px, 30vw, 340px)' }}>
          <img
            src={blog.heroImg ? imgUrl(blog.heroImg) : '/images/about_us.jpg'}
            alt={blog.title}
            className="w-full h-full object-cover object-center"
          />
        </div>
      </div>

      {/* Article body — same horizontal padding as header/footer */}
      <article className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 lg:px-20 pt-8 md:pt-10 pb-10 md:pb-14">

        {/* Back button */}
        <button onClick={() => navigate('/all-blogs')}
          className="flex items-center gap-2 font-body text-[10px] tracking-[0.2em] uppercase text-stone-400 hover:text-gold transition-colors mb-8">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          All Blogs
        </button>

        {/* Title */}
        <h1 className="font-display font-normal text-stone-900 leading-tight mb-4 max-w-4xl"
          style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)' }}>
          {blog.title}
        </h1>

        {/* Meta */}
        <div className="flex items-center gap-3 mb-6">
          {displayDate && <span className="font-body text-gold text-xs tracking-widest uppercase">{displayDate}</span>}
          {blog.tag && (
            <>
              <span className="w-1 h-1 rounded-full bg-stone-300" />
              <span className="font-body text-stone-400 text-xs tracking-widest uppercase">{blog.tag}</span>
            </>
          )}
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-stone-100 mb-8" />

        {/* Content blocks — full width matching page layout */}
        <div className="flex flex-col gap-5">
          {(blog.content || []).map((block, i) => <ContentBlock key={i} block={block} index={i} />)}
        </div>
      </article>

      {/* More Blogs */}
      <BlogsNews title="More Blogs & News" showTag={false} />
    </div>
  );
}
